import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

// Placeholder image
const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1200&q=80";

// Shared animation presets
const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const fadeUpInView = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const scaleIn = { initial: { opacity: 0, scale: 0.95 }, whileInView: { opacity: 1, scale: 1 }, viewport: { once: true } };
const dur08 = { duration: 0.8 };
const dur06 = { duration: 0.6 };
const dur07 = { duration: 0.7 };

const Services = () => {
  const bathroomServices = [
    "Full Bathroom Installations",
    "Wet Rooms",
    "En-Suites",
    "Bespoke Wall & Floor Tiling",
    "Shower & Bath Installations",
    "Lighting & Finishing Details"
  ];

  const refurbishmentServices = [
    "Kitchen Installations",
    "Flooring & Carpentry",
    "Plastering & Structural Alterations",
    "Room Renovations",
    "Interior Upgrades"
  ];

  return (
    <div data-testid="services-page">
      {/* Intro Section */}
      <section className="py-24 bg-charcoal" data-testid="services-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.h1
            {...fadeUp}
            transition={dur08}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-stone-50 mb-6"
          >
            Our Services
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...dur08, delay: 0.2 }}
            className="font-body text-base md:text-lg text-stone-300 max-w-2xl mx-auto"
          >
            Luxury bathrooms and high-quality residential refurbishment projects.
          </motion.p>
        </div>
      </section>

      {/* Luxury Bathrooms Section */}
      <section id="bathrooms" className="scroll-mt-24" data-testid="bathrooms-section">
        {/* Banner Image */}
        <div className="relative h-[50vh] overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/o3acfkp4_pawiale_Ultra-realistic_luxury_bathroom_renovation_walk-in_sh_846b3633-73d0-4f37-af98-b865339690f6_3.png"
            alt="Luxury bathroom"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-charcoal/30" />
          <div className="absolute inset-0 flex items-center justify-center">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-stone-50">
              Luxury Bathrooms
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="section-padding bg-stone-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-base md:text-lg text-charcoal max-w-3xl mb-12"
            >
              Complete bathroom design and installation tailored to your space and finished to exceptional standards.
            </motion.p>

            {/* Services Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-16">
              {bathroomServices.map((service, index) => (
                <motion.div
                  key={service}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="border-l-2 border-bronze/30 pl-4"
                  data-testid={`bathroom-service-${index}`}
                >
                  <p className="font-body text-sm md:text-base text-charcoal">
                    {service}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Work Examples Gallery */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[
                { src: "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/89id2yci_20251214_002517.jpg", label: "Herringbone Style Tiling" },
                { src: "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/04qvrzby_20251109_121653.jpg", label: "Custom Bath Installation & Custom Niche with LED" },
                { src: "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/a3n3lid5_Floor%20Mat%20Color%20Change.png", label: "Full Bathroom Tiling & Custom Mosaic Tiles in Shower" },
                { src: "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/2aidfnmr_20260209_184149.jpg", label: "Wet Room Refurbishment" },
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative aspect-square overflow-hidden rounded-sm"
                  data-testid={`bathroom-work-example-${index}`}
                >
                  <img
                    src={item.src}
                    alt={item.label}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
                  <p className="absolute bottom-3 left-3 right-3 font-body text-xs md:text-sm text-stone-50 tracking-wide">
                    {item.label}
                  </p>
                </motion.div>
              ))}
            </div>

            <Link
              to="/portfolio"
              data-testid="view-bathroom-projects-btn"
              className="btn-secondary inline-flex items-center gap-2"
            >
              View Bathroom Projects
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-10 font-body text-sm md:text-base text-grey leading-relaxed max-w-3xl italic"
              data-testid="bathroom-luxury-statement"
            >
              Every bathroom we deliver is one of a kind. From bespoke tiling patterns and custom niches to freestanding centrepieces and precision-fit wet rooms, PJMW Services specialises in the intricate, detail-driven work that transforms an ordinary space into something truly exceptional. No two projects are ever the same — and that's exactly how we like it.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="h-px bg-border" />

      {/* Residential Refurbishment Section */}
      <section id="refurbishment" className="scroll-mt-24" data-testid="refurbishment-section">
        {/* Banner Image */}
        <div className="relative h-[50vh] overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/q93js15i_pawiale_Luxury_home_renovation_interior_open-plan_living_spac_ba6d5b33-aea0-4fe2-a691-13b8180dc4a0_3.png"
            alt="Home refurbishment"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-charcoal/30" />
          <div className="absolute inset-0 flex items-center justify-center">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-stone-50">
              Residential Refurbishment
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="section-padding bg-stone-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-base md:text-lg text-charcoal max-w-3xl mb-12"
            >
              High-quality refurbishment works designed to modernise and elevate your home.
            </motion.p>

            {/* Services Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-16">
              {refurbishmentServices.map((service, index) => (
                <motion.div
                  key={service}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="border-l-2 border-bronze/30 pl-4"
                  data-testid={`refurbishment-service-${index}`}
                >
                  <p className="font-body text-sm md:text-base text-charcoal">
                    {service}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Work Examples Gallery */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[
                { src: "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/oyz1873r_20260211_162832.jpg", label: "Conservatory to Media Wall Conversion" },
                { src: "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/w08f81l4_20191202_211443.jpg", label: "Custom Fireplace Featured Wall in Living Room" },
                { src: "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/8u29rv31_20230709_202240.jpg", label: "Complete Kitchen Conversion" },
                { src: "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/jhsxi9ei_20190701_221329.jpg", label: "Full Kitchen Installation and New Flooring" },
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative aspect-square overflow-hidden rounded-sm"
                  data-testid={`refurbishment-work-example-${index}`}
                >
                  <img
                    src={item.src}
                    alt={item.label}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
                  <p className="absolute bottom-3 left-3 right-3 font-body text-xs md:text-sm text-stone-50 tracking-wide">
                    {item.label}
                  </p>
                </motion.div>
              ))}
            </div>

            <Link
              to="/portfolio"
              data-testid="view-refurbishment-projects-btn"
              className="btn-secondary inline-flex items-center gap-2"
            >
              View Refurbishment Projects
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-10 font-body text-sm md:text-base text-grey leading-relaxed max-w-3xl italic"
              data-testid="refurbishment-luxury-statement"
            >
              Whether it's a show-stopping media wall with integrated lighting or a straightforward kitchen refit, PJMW Services brings the same level of precision and craftsmanship to every project. From bespoke feature walls and full room conversions to essential repairs and everyday refurbishments, no job is too bold or too basic — we simply deliver it to the highest standard, every time.
            </motion.p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-charcoal py-20 px-6" data-testid="services-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-heading text-3xl md:text-4xl text-stone-50 mb-4"
          >
            Interested in Working With Us?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-body text-sm md:text-base text-stone-300 mb-8 leading-relaxed"
          >
            Get in touch to discuss your project. We'd love to hear your ideas and show you what's possible.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <Link
              to="/contact"
              data-testid="services-cta-contact-btn"
              className="inline-flex items-center gap-2 px-8 py-3 bg-bronze text-stone-50 font-body text-sm tracking-widest uppercase hover:bg-bronze/90 transition-colors duration-300"
            >
              Contact Us
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
